import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useMutation } from '@apollo/client/react';
import {
  DimeButton,
  DimeCard,
  DimeCardContent,
  DimeCardDescription,
  DimeCardFooter,
  DimeCardHeader,
  DimeCardTitle,
  DimeSpinner,
} from '@nudool/design-system';
import { useGoogleLogin } from '@react-oauth/google';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { GoogleIcon } from '../modules/iam/google-icon.js';
import { LoginBrandPanel } from '../modules/iam/login-brand-panel.js';
import { LoginMobileHeader } from '../modules/iam/login-mobile-header.js';
import { LOGIN_WITH_GOOGLE } from '../modules/iam/operations/index.js';

const loginButtonClassName =
  'h-11 w-full text-base lg:h-8 lg:text-sm bg-neutral-900 text-white hover:bg-neutral-800';

// eslint-disable-next-line react-refresh/only-export-components
function LoginPage() {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [loginWithGoogle, { loading: googleLoading }] = useMutation(LOGIN_WITH_GOOGLE, {
    onCompleted: (data) => {
      if (data.loginWithGoogle) {
        navigate({ to: '/' });
      } else {
        setErrorMessage('Sign in failed. Your account may not have access.');
      }
    },
    onError: (error) =>
      setErrorMessage(
        CombinedGraphQLErrors.is(error)
          ? (error.errors[0]?.message ?? 'Something went wrong. Please try again.')
          : 'Something went wrong. Please try again.',
      ),
  });

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: (response) => {
      setErrorMessage(null);
      loginWithGoogle({ variables: { code: response.code } });
    },
    onError: () => setErrorMessage('Google sign in was cancelled or failed.'),
  });

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2">
      <LoginBrandPanel />

      <div className="relative flex min-h-screen flex-col bg-gradient-to-br from-background via-muted/20 to-muted/40 lg:items-center lg:justify-center lg:p-10">
        <LoginMobileHeader />

        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 hidden bg-[radial-gradient(circle_at_1px_1px,var(--border)_1px,transparent_0)] bg-[length:24px_24px] opacity-30 lg:block"
        />

        <div className="relative z-10 -mt-10 flex w-full max-lg:flex-1 flex-col rounded-t-[2rem] bg-card px-5 py-8 shadow-[0_-10px_40px_rgba(0,0,0,0.08)] sm:px-8 lg:mt-0 lg:w-full lg:max-w-md lg:rounded-none lg:bg-transparent lg:p-0 lg:shadow-none">
          <DimeCard className="relative flex w-full max-w-md max-lg:min-h-full max-lg:flex-1 flex-col border-0 bg-transparent shadow-none ring-0 lg:rounded-xl lg:border lg:bg-card lg:shadow-xl lg:ring-1 lg:ring-foreground/5 lg:animate-in lg:fade-in lg:slide-in-from-bottom-4 lg:duration-500">
            <DimeCardHeader className="px-0 text-left lg:px-(--card-spacing)">
              <DimeCardTitle className="text-2xl">Welcome back</DimeCardTitle>
              <DimeCardDescription>Sign in to your workspace</DimeCardDescription>
            </DimeCardHeader>

            <DimeCardContent className="flex flex-col gap-5 px-0 lg:px-(--card-spacing)">
              {errorMessage && (
                <p
                  role="alert"
                  className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {errorMessage}
                </p>
              )}

              <DimeButton
                variant="outline"
                className={`${loginButtonClassName} bg-white text-neutral-900 hover:bg-neutral-50`}
                disabled={googleLoading}
                onClick={() => googleLogin()}
              >
                {googleLoading ? (
                  <DimeSpinner className="size-5" />
                ) : (
                  <>
                    <GoogleIcon />
                    Continue with Google
                  </>
                )}
              </DimeButton>
            </DimeCardContent>

            <DimeCardFooter className="max-lg:mt-auto flex-col gap-4 border-t-0 bg-transparent px-0 pb-8 pt-10 max-lg:bg-transparent lg:justify-center lg:px-(--card-spacing) lg:pb-(--card-spacing) lg:pt-4 lg:bg-muted/50">
              <p className="max-w-xs text-center text-xs leading-relaxed text-neutral-700 lg:max-w-none">
                Sign in with your organization Google account.
              </p>
              <p className="text-center text-xs text-neutral-600 lg:hidden">
                &copy; {new Date().getFullYear()} Nudool
              </p>
            </DimeCardFooter>
          </DimeCard>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/login')({
  component: LoginPage,
});
